import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { FuaAssessmentQuestionnaireItem } from 'components/Assessments/FuaAssessment/types/questionnaire'
import { SupervisoryReviewFormField } from './formField'

/**
 * Supervisory Review subsection adapter for FUA Assessment
 * Handles supervisory-review extraction and transformation
 */
export class SupervisoryReviewAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const idBadge = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE
    )?.at(0)

    const honoringRights = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS
    )?.at(0)

    const emergencyProtocol = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL
    )?.at(0)

    const hygienePrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS
    )?.at(0)

    const reportingChanges = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES
    )?.at(0)

    const grievanceComplaint = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT
    )?.at(0)

    const pocDocReviewedWithCaregiver = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER
    )?.at(0)

    const ppeAccess = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS
    )?.at(0)

    const supervisionObserved = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED
    )?.at(0)

    const taskObserved = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED
    )?.at(0)

    const patientStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS
    )?.at(0)

    const comments = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS
    )?.at(0)

    const caregiverEducationTopic = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_CAREGIVER_EDUCATION_TOPIC
    )?.at(0)

    const educationProvided = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_EDUCATION_PROVIDED
    )?.at(0)

    const initialsAuthorization = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_INITIALS_AUTHORIZATION
    )?.at(0)

    return {
      [SupervisoryReviewFormField.ID_BADGE]: idBadge,
      [SupervisoryReviewFormField.HONORING_RIGHTS]: honoringRights,
      [SupervisoryReviewFormField.EMERGENCY_PROTOCOL]: emergencyProtocol,
      [SupervisoryReviewFormField.HYGIENE_PRECAUTIONS]: hygienePrecautions,
      [SupervisoryReviewFormField.REPORTING_CHANGES]: reportingChanges,
      [SupervisoryReviewFormField.GRIEVANCE_COMPLAINT]: grievanceComplaint,
      [SupervisoryReviewFormField.POC_DOC_REVIEWED_WITH_CAREGIVER]: pocDocReviewedWithCaregiver,
      [SupervisoryReviewFormField.PPE_ACCESS]: ppeAccess,
      [SupervisoryReviewFormField.SUPERVISION_OBSERVED]: supervisionObserved,
      [SupervisoryReviewFormField.TASK_OBSERVED]: taskObserved,
      [SupervisoryReviewFormField.PATIENT_STATUS]: patientStatus,
      [SupervisoryReviewFormField.COMMENTS]: comments,
      [SupervisoryReviewFormField.EDUCATION_CAREGIVER_EDUCATION_TOPIC]: caregiverEducationTopic,
      [SupervisoryReviewFormField.EDUCATION_EDUCATION_PROVIDED]: educationProvided,
      [SupervisoryReviewFormField.INITIALS_AUTHORIZATION]: initialsAuthorization
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(SupervisoryReviewFormField.ID_BADGE)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE,
        value: this.getFormDataField(SupervisoryReviewFormField.ID_BADGE)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.HONORING_RIGHTS)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS,
        value: this.getFormDataField(SupervisoryReviewFormField.HONORING_RIGHTS)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.EMERGENCY_PROTOCOL)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL,
        value: this.getFormDataField(SupervisoryReviewFormField.EMERGENCY_PROTOCOL)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.HYGIENE_PRECAUTIONS)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS,
        value: this.getFormDataField(SupervisoryReviewFormField.HYGIENE_PRECAUTIONS)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.REPORTING_CHANGES)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES,
        value: this.getFormDataField(SupervisoryReviewFormField.REPORTING_CHANGES)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.GRIEVANCE_COMPLAINT)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT,
        value: this.getFormDataField(SupervisoryReviewFormField.GRIEVANCE_COMPLAINT)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.POC_DOC_REVIEWED_WITH_CAREGIVER)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER,
        value: this.getFormDataField(SupervisoryReviewFormField.POC_DOC_REVIEWED_WITH_CAREGIVER)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.PPE_ACCESS)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS,
        value: this.getFormDataField(SupervisoryReviewFormField.PPE_ACCESS)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.SUPERVISION_OBSERVED)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED,
        value: this.getFormDataField(SupervisoryReviewFormField.SUPERVISION_OBSERVED)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.TASK_OBSERVED)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED,
        value: this.getFormDataField(SupervisoryReviewFormField.TASK_OBSERVED)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.PATIENT_STATUS)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS,
        value: this.getFormDataField(SupervisoryReviewFormField.PATIENT_STATUS)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.COMMENTS)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS,
        value: this.getFormDataField(SupervisoryReviewFormField.COMMENTS)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.EDUCATION_CAREGIVER_EDUCATION_TOPIC)) {
      mutations.push({
        linkId:
          FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_CAREGIVER_EDUCATION_TOPIC,
        value: this.getFormDataField(SupervisoryReviewFormField.EDUCATION_CAREGIVER_EDUCATION_TOPIC)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.EDUCATION_EDUCATION_PROVIDED)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_EDUCATION_PROVIDED,
        value: this.getFormDataField(SupervisoryReviewFormField.EDUCATION_EDUCATION_PROVIDED)
      })
    }

    if (this.formDataFieldExists(SupervisoryReviewFormField.INITIALS_AUTHORIZATION)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_INITIALS_AUTHORIZATION,
        value: this.getFormDataField(SupervisoryReviewFormField.INITIALS_AUTHORIZATION)
      })
    }

    return mutations
  }
}
